import { useEffect, useState } from 'react';
import { payrollAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Download, Wallet, TrendingUp, TrendingDown, Calendar, User, Building2, FileText, IndianRupee } from 'lucide-react';

const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

export default function MyPayrollPage() {
  const { user } = useAuth();
  const [payrollData, setPayrollData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const fetchPayroll = async () => {
    setLoading(true);
    try {
      const res = await payrollAPI.getMyPayroll({ month: selectedMonth, year: selectedYear });
      setPayrollData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch payroll data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayroll();
  }, [selectedMonth, selectedYear]);

  const handleDownloadPayslip = async (month, year) => {
    setDownloading(true);
    try {
      const response = await payrollAPI.downloadPayslip(month, year);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Payslip_${payrollData?.employee?.employee_id || 'EMP'}_${months.find(m => m.value === month)?.label}_${year}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Payslip downloaded successfully');
    } catch (error) {
      toast.error('Failed to download payslip');
    } finally {
      setDownloading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const { employee, current_month, ytd_summary, history } = payrollData || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">My Payroll</h1>
          <p className="text-muted-foreground mt-1">View your salary details and download payslips</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-24">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Employee Info Card */}
      {employee && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Employee Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Employee ID</p>
                <p className="font-medium">{employee.employee_id}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Name</p>
                <p className="font-medium">{employee.name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Department</p>
                <p className="font-medium">{employee.department}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Designation</p>
                <p className="font-medium">{employee.designation}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">PAN</p>
                <p className="font-medium">{employee.pan_number || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Bank Account</p>
                <p className="font-medium">{employee.bank_account ? `****${employee.bank_account.slice(-4)}` : 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-green-200 bg-green-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-green-700">
              <Wallet className="w-4 h-4" />
              Gross Salary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-700">{formatCurrency(current_month?.gross_salary || 0)}</p>
            <p className="text-xs text-muted-foreground mt-1">This month</p>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-red-700">
              <TrendingDown className="w-4 h-4" />
              Total Deductions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-red-700">{formatCurrency(current_month?.total_deductions || 0)}</p>
            <p className="text-xs text-muted-foreground mt-1">PF + PT + TDS</p>
          </CardContent>
        </Card>

        <Card className="border-primary/30 bg-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-primary">
              <IndianRupee className="w-4 h-4" />
              Net Salary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-primary">{formatCurrency(current_month?.net_salary || 0)}</p>
            <p className="text-xs text-muted-foreground mt-1">Take home pay</p>
          </CardContent>
        </Card>

        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
              <TrendingUp className="w-4 h-4" />
              YTD Earnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-blue-700">{formatCurrency(ytd_summary?.total_net || 0)}</p>
            <p className="text-xs text-muted-foreground mt-1">{ytd_summary?.months_processed} months in {ytd_summary?.year}</p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed View */}
      <Tabs defaultValue="breakdown">
        <TabsList>
          <TabsTrigger value="breakdown">Salary Breakdown</TabsTrigger>
          <TabsTrigger value="history">Payment History</TabsTrigger>
          <TabsTrigger value="ytd">YTD Summary</TabsTrigger>
        </TabsList>

        <TabsContent value="breakdown">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">
                  {months.find(m => m.value === selectedMonth)?.label} {selectedYear} Breakdown
                </CardTitle>
                <CardDescription>Detailed salary components</CardDescription>
              </div>
              <Button onClick={() => handleDownloadPayslip(selectedMonth, selectedYear)} disabled={downloading} data-testid="download-payslip-btn">
                <Download className="w-4 h-4 mr-2" />
                {downloading ? 'Downloading...' : 'Download Payslip'}
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-8">
                {/* Earnings */}
                <div>
                  <h3 className="font-semibold mb-4 text-green-700 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" /> Earnings
                  </h3>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>Basic Salary</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(current_month?.basic_salary || 0)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>House Rent Allowance (HRA)</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(current_month?.hra || 0)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Special Allowance</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(current_month?.special_allowance || 0)}</TableCell>
                      </TableRow>
                      <TableRow className="bg-green-50">
                        <TableCell className="font-bold">Gross Salary</TableCell>
                        <TableCell className="text-right font-bold text-green-700">{formatCurrency(current_month?.gross_salary || 0)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                {/* Deductions */}
                <div>
                  <h3 className="font-semibold mb-4 text-red-700 flex items-center gap-2">
                    <TrendingDown className="w-4 h-4" /> Deductions
                  </h3>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>Provident Fund (PF)</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(current_month?.pf_deduction || 0)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Professional Tax (PT)</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(current_month?.pt_deduction || 0)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>TDS (Tax Deducted at Source)</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(current_month?.tds_deduction || 0)}</TableCell>
                      </TableRow>
                      <TableRow className="bg-red-50">
                        <TableCell className="font-bold">Total Deductions</TableCell>
                        <TableCell className="text-right font-bold text-red-700">{formatCurrency(current_month?.total_deductions || 0)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Net Salary */}
              <div className="mt-6 p-4 bg-primary/10 rounded-lg flex items-center justify-between">
                <span className="text-lg font-semibold">Net Salary Payable</span>
                <span className="text-2xl font-bold text-primary">{formatCurrency(current_month?.net_salary || 0)}</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Payment History - {selectedYear}
              </CardTitle>
              <CardDescription>Year to date payment records</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Deductions</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Payslip</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history?.map((item) => (
                    <TableRow key={item.month}>
                      <TableCell className="font-medium">{item.month_name}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.gross_salary)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatCurrency(item.total_deductions)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(item.net_salary)}</TableCell>
                      <TableCell>
                        <Badge variant={item.status === 'paid' ? 'default' : 'secondary'} className={item.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                          {item.status === 'paid' ? 'Paid' : 'Processing'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadPayslip(item.month, item.year)}>
                          <FileText className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ytd">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Year to Date Summary - {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600 mb-1">Total Gross Earnings</p>
                  <p className="text-2xl font-bold text-green-700">{formatCurrency(ytd_summary?.total_gross || 0)}</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-600 mb-1">Total Deductions</p>
                  <p className="text-2xl font-bold text-red-700">{formatCurrency(ytd_summary?.total_deductions || 0)}</p>
                </div>
                <div className="p-4 bg-primary/10 rounded-lg">
                  <p className="text-sm text-primary mb-1">Total Net Earnings</p>
                  <p className="text-2xl font-bold text-primary">{formatCurrency(ytd_summary?.total_net || 0)}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                Summary for {ytd_summary?.months_processed} months processed in {ytd_summary?.year}
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
